import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesApi, warrantyPlanApi, uploadApi, phoneBrandsApi, tenantApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { AutoSuggest } from '../../components/AutoSuggest';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Plus, Trash2, Loader2, CheckCircle, XCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const emptyRow = () => ({
  key: Math.random(),
  customer_name: '', customer_phone: '', device_brand: '', device_model: '',
  device_imei: '', device_price: '', warranty_attached: true, warranty_plan_id: '',
  warranty_price_override: '', attachments: [], uploading: false, errors: {},
});

export default function BulkSalePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [allPlans, setAllPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [deviceBrands, setDeviceBrands] = useState([]);
  const [brandsData, setBrandsData] = useState([]);
  const [tenantContractType, setTenantContractType] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [plansRes, brandsRes] = await Promise.all([
          warrantyPlanApi.list({ active_only: true }),
          phoneBrandsApi.list(),
        ]);
        setAllPlans(Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data.items || []));
        const brandsArr = Array.isArray(brandsRes.data) ? brandsRes.data : (brandsRes.data.items || []);
        setBrandsData(brandsArr);
        setDeviceBrands(brandsArr.map(b => b.name));
        
        // Fetch tenant contract type
        if (user?.tenant_id) {
          const { data: tenant } = await tenantApi.get(user.tenant_id);
          setTenantContractType(tenant.contract_type || 'NON_EXCLUSIVE');
        }
      } catch {
        // silently fail
      }
    };
    loadData();
  }, [user]);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      if (updated.errors[field]) {
        updated.errors = { ...updated.errors };
        delete updated.errors[field];
      }
      return updated;
    }));
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const getEligiblePlans = (price, brandName) => {
    const p = parseFloat(price) || 0;
    if (p <= 0) return [];
    const selectedBrand = brandsData.find(b => b.name === brandName);
    const brandOs = selectedBrand?.os_type || null;
    return allPlans.filter(plan => {
      const priceMatch = p >= plan.device_price_min && p <= plan.device_price_max;
      if (!priceMatch) return false;
      // Filter by tenant contract type
      if (tenantContractType) {
        const ct = plan.contract_type || 'ALL';
        if (ct !== 'ALL' && ct !== tenantContractType) return false;
      }
      // Filter by OS type
      if (brandOs && brandOs !== 'BOTH') {
        const planOs = plan.os_type || 'BOTH';
        if (planOs !== 'BOTH' && planOs !== brandOs) return false;
      }
      return true;
    });
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      if (file.type === 'application/pdf') { resolve(file); return; }
      const img = new Image();
      const canvas = document.createElement('canvas');
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', quality);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileUpload = async (idx, e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    updateRow(idx, 'uploading', true);
    try {
      const uploaded = [];
      for (const file of files) {
        const compressed = await compressImage(file);
        const { data } = await uploadApi.upload(compressed);
        uploaded.push(data.id);
      }
      setRows(prev => prev.map((r, i) => i === idx ? { ...r, attachments: [...r.attachments, ...uploaded], uploading: false } : r));
    } catch {
      toast.error('Upload failed');
      updateRow(idx, 'uploading', false);
    }
    e.target.value = '';
  };

  const validateAll = () => {
    let valid = true;
    const updated = rows.map(r => {
      if (!r.customer_name && !r.customer_phone && !r.device_brand && !r.device_imei && !r.device_price) return r;
      const errors = {};
      if (!r.customer_name.trim()) errors.customer_name = true;
      if (!r.customer_phone || r.customer_phone.length < 10) errors.customer_phone = true;
      if (!r.device_brand) errors.device_brand = true;
      if (!r.device_model.trim()) errors.device_model = true;
      if (!r.device_imei || r.device_imei.length !== 15) errors.device_imei = true;
      if (!r.device_price || parseFloat(r.device_price) <= 0) errors.device_price = true;
      if (r.warranty_attached && !r.warranty_plan_id) errors.warranty_plan_id = true;
      if (r.warranty_attached && (!r.warranty_price_override || parseFloat(r.warranty_price_override) <= 0)) errors.warranty_price_override = true;
      if (Object.keys(errors).length > 0) valid = false;
      return { ...r, errors };
    });
    setRows(updated);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      toast.error('Please fix highlighted fields before submitting');
      return;
    }
    const validRows = rows.filter(r => r.customer_name && r.device_imei);
    if (validRows.length === 0) {
      toast.error('Fill at least one complete row');
      return;
    }
    setSubmitting(true);
    try {
      const payload = validRows.map(r => ({
        customer_name: r.customer_name,
        customer_phone: r.customer_phone,
        device_brand: r.device_brand,
        device_model: r.device_model,
        device_imei: r.device_imei,
        device_price: parseFloat(r.device_price),
        warranty_attached: r.warranty_attached,
        warranty_plan_id: r.warranty_attached ? r.warranty_plan_id : null,
        warranty_price_override: (r.warranty_attached && r.warranty_price_override) ? parseFloat(r.warranty_price_override) : null,
        attachments: r.attachments,
      }));
      const { data } = await salesApi.createBulk(payload);
      setResults(data);
      toast.success(data.message);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Bulk upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const errCls = (row, field) => row.errors?.[field] ? 'border-red-400 bg-red-50' : '';

  if (results) {
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto space-y-6">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Bulk Entry Results</h1>
          <Card className="border-slate-200"><CardContent className="pt-6 space-y-3">
            <p className="text-lg font-medium">{results.message}</p>
            {results.results?.map((r, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                {r.status === 'success' ? <CheckCircle className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                <span className="text-sm">Row {r.row}: {r.status === 'success' ? `Created (${r.approval_status})${r.warranty_certificate ? ' - Cert: ' + r.warranty_certificate : ''}` : r.detail}</span>
              </div>
            ))}
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setResults(null); setRows([emptyRow(), emptyRow(), emptyRow()]); }} className="bg-[#005DE8] hover:bg-[#0047B3]">New Bulk Entry</Button>
              <Button variant="outline" onClick={() => navigate('/sales/history')}>View Sales</Button>
            </div>
          </CardContent></Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Bulk Sale Entry</h1>
            <p className="text-slate-600 mt-1">Add multiple sales at once</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={addRow} data-testid="add-row-btn"><Plus className="h-4 w-4 mr-2" />Add Row</Button>
            <Button onClick={handleSubmit} className="bg-[#005DE8] hover:bg-[#0047B3]" disabled={submitting} data-testid="bulk-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Submit All ({rows.filter(r => r.customer_name && r.device_imei).length})
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {rows.map((row, idx) => (
            <Card key={row.key} className="border-slate-200">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Sale #{idx + 1}</CardTitle>
                <div className="flex items-center gap-2">
                  {Object.keys(row.errors || {}).length > 0 && <Badge className="bg-red-50 text-red-700 border-red-200 border text-xs">Fix errors</Badge>}
                  {rows.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} className="text-red-500 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-4 px-4 space-y-3">
                {/* Row 1: Device & Customer */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Customer *</Label>
                    <Input value={row.customer_name} onChange={(e) => updateRow(idx, 'customer_name', e.target.value)} placeholder="Name" className={`h-9 text-sm ${errCls(row, 'customer_name')}`} data-testid={`bulk-name-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Phone *</Label>
                    <Input value={row.customer_phone} onChange={(e) => updateRow(idx, 'customer_phone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Phone" className={`h-9 text-sm ${errCls(row, 'customer_phone')}`} data-testid={`bulk-phone-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Brand *</Label>
                    <AutoSuggest
                      options={deviceBrands}
                      value={row.device_brand}
                      onChange={(v) => updateRow(idx, 'device_brand', v)}
                      placeholder="Brand"
                      testId={`bulk-brand-${idx}`}
                      className={errCls(row, 'device_brand') ? 'ring-1 ring-red-400 rounded-md' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Model *</Label>
                    <Input value={row.device_model} onChange={(e) => updateRow(idx, 'device_model', e.target.value)} placeholder="Model" className={`h-9 text-sm ${errCls(row, 'device_model')}`} data-testid={`bulk-model-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IMEI *</Label>
                    <Input value={row.device_imei} onChange={(e) => updateRow(idx, 'device_imei', e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="15 digits" className={`h-9 text-sm ${errCls(row, 'device_imei')}`} data-testid={`bulk-imei-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Price *</Label>
                    <Input type="number" value={row.device_price} onChange={(e) => updateRow(idx, 'device_price', e.target.value)} placeholder="INR" className={`h-9 text-sm ${errCls(row, 'device_price')}`} data-testid={`bulk-price-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Attach</Label>
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => handleFileUpload(idx, e)} />
                        <div className="h-9 w-9 rounded-md border flex items-center justify-center hover:bg-slate-50">
                          {row.uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3 text-slate-500" />}
                        </div>
                      </label>
                      {row.attachments.length > 0 && <Badge variant="outline" className="text-xs">{row.attachments.length}</Badge>}
                    </div>
                  </div>
                </div>
                {/* Row 2: Warranty */}
                <div className="flex items-center gap-4 bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={row.warranty_attached} onCheckedChange={(v) => {
                      updateRow(idx, 'warranty_attached', v);
                      if (!v) { updateRow(idx, 'warranty_plan_id', ''); updateRow(idx, 'warranty_price_override', ''); }
                    }} data-testid={`bulk-warranty-toggle-${idx}`} />
                    <Label className="text-xs text-slate-600">Warranty</Label>
                  </div>
                  {row.warranty_attached && (
                    <>
                      <div className="flex-1 max-w-xs">
                        <Select value={row.warranty_plan_id || undefined} onValueChange={(v) => updateRow(idx, 'warranty_plan_id', v)}>
                          <SelectTrigger className={`h-8 text-xs ${errCls(row, 'warranty_plan_id')}`} data-testid={`bulk-plan-${idx}`}>
                            <SelectValue placeholder="Select plan" />
                          </SelectTrigger>
                          <SelectContent>
                            {getEligiblePlans(row.device_price, row.device_brand).length > 0 ? (
                              getEligiblePlans(row.device_price, row.device_brand).map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.plan_name} - {formatCurrency(p.warranty_price)}{p.os_type && p.os_type !== 'BOTH' ? ` (${p.os_type})` : ''}</SelectItem>
                              ))
                            ) : (
                              <SelectItem value="__none" disabled>
                                {row.device_price && row.device_brand ? 'No plans for this brand/price' : row.device_price ? 'Select brand first' : 'Enter device price first'}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {row.warranty_plan_id && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Base: {formatCurrency(allPlans.find(p => p.id === row.warranty_plan_id)?.warranty_price || 0)}</span>
                          <Input
                            type="number"
                            value={row.warranty_price_override}
                            onChange={(e) => updateRow(idx, 'warranty_price_override', e.target.value)}
                            placeholder="Override *"
                            className={`h-8 w-28 text-xs ${errCls(row, 'warranty_price_override')}`}
                            min={allPlans.find(p => p.id === row.warranty_plan_id)?.warranty_price || 0}
                            data-testid={`bulk-override-${idx}`}
                            required
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
